import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeyUp, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    // [Archies a11y H1] Close the nearest OPEN details (an open submenu first, else the drawer). The nearest details
    // of a focused submenu toggle is that submenu's own — closed — details, so Escape used to do nothing there.
    // Listened on document keydown (capture) while open: other Escape handlers inside the drawer (e.g. the category
    // buttons) moved focus to <body> on keydown, so this element's own keyup never fired.
    const target = event.target instanceof Element ? event.target : null;
    const details = target?.closest('details[open]');
    this.#close(details instanceof HTMLDetailsElement && this.contains(details) ? details : this.refs.details);
    event.stopPropagation(); // handled: don't let an inner Escape handler also run and drop focus to <body>
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
    if (details === this.refs.details) this.#makeModal(summary);
  }

  /**
   * [Archies a11y H1] Make the open main drawer modal: everything outside it is `inert` (so screen-reader browse mode
   * can't wander into the page behind), except the Menu/close toggle. Late-injected body children (e.g. the Gorgias
   * chat button) are caught by a MutationObserver. Reversed — with focus back on the toggle — in #release().
   * @param {HTMLElement} summary
   */
  #makeModal(summary) {
    if (this.#inerted) return;
    const panel = this.refs.details.querySelector('[data-header-drawer]');
    if (!(panel instanceof HTMLElement)) return;
    this.#inerted = [];
    const makeInert = (/** @type {Element} */ el) => {
      if (!(el instanceof HTMLElement) || el.inert || el.tagName === 'SCRIPT' || el === summary || el.contains(summary) || el.contains(panel)) return;
      el.inert = true;
      this.#inerted?.push(el);
    };
    for (let el = panel; el && el !== document.body; el = el.parentElement) {
      if (!el.parentElement) break;
      Array.from(el.parentElement.children).forEach((sibling) => sibling !== el && makeInert(sibling));
    }
    document.addEventListener('keydown', this.#onKeyUp, true);
    this.#bodyObserver = new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach((n) => n instanceof Element && makeInert(n))));
    this.#bodyObserver.observe(document.body, { childList: true });
  }

  /** @param {HTMLElement | null} summary */
  #release(summary) {
    document.removeEventListener('keydown', this.#onKeyUp, true);
    this.#bodyObserver?.disconnect();
    this.#bodyObserver = null;
    this.#inerted?.forEach((el) => (el.inert = false));
    this.#inerted = null;
    // Focus back on the toggle unless the shopper already moved it somewhere else (e.g. followed a link).
    const active = document.activeElement;
    if (summary && (!active || active === document.body || this.contains(active))) summary.focus({ preventScroll: true });
  }

  /** @type {HTMLElement[] | null} */
  #inerted = null;
  /** @type {MutationObserver | null} */
  #bodyObserver = null;

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        this.#release(summary);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
